import { createSignal, onMount, onCleanup } from 'solid-js';
import { RESPONSIVE, PERFORMANCE } from '../constants/app-constants';

/**
 * Custom hook for responsive design utilities
 * Provides reactive breakpoint detection and responsive utilities
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = createSignal({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  const [isMobile, setIsMobile] = createSignal(RESPONSIVE.isMobile());
  const [isTablet, setIsTablet] = createSignal(RESPONSIVE.isTablet());
  const [isDesktop, setIsDesktop] = createSignal(RESPONSIVE.isDesktop());

  // Debounce helper to prevent excessive updates
  let resizeTimeout = null;

  const updateScreenSize = () => {
    const newSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    setScreenSize(newSize);
    setIsMobile(RESPONSIVE.isMobile());
    setIsTablet(RESPONSIVE.isTablet());
    setIsDesktop(RESPONSIVE.isDesktop());
  };

  const debouncedUpdateScreenSize = () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout);
    }
    resizeTimeout = setTimeout(() => {
      updateScreenSize();
      resizeTimeout = null;
    }, PERFORMANCE.debounce.resize);
  };

  onMount(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', debouncedUpdateScreenSize);
      updateScreenSize(); // Initial call
    }
  });

  onCleanup(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', debouncedUpdateScreenSize);
      // Clear any pending timeout
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }
    }
  });

  // Utility functions for responsive styling
  const responsive = {
    // Get value based on screen size
    getValue: (mobileValue, tabletValue, desktopValue) => {
      if (isMobile()) return mobileValue;
      if (isTablet()) return tabletValue || mobileValue;
      return desktopValue || tabletValue || mobileValue;
    },

    // Get responsive styles
    getStyle: (mobileStyles, tabletStyles = {}, desktopStyles = {}) => {
      if (isMobile()) return mobileStyles;
      if (isTablet()) return { ...mobileStyles, ...tabletStyles };
      return { ...mobileStyles, ...tabletStyles, ...desktopStyles };
    },

    // Get responsive class names
    getClassName: (mobileClass, tabletClass = '', desktopClass = '') => {
      const classes = [mobileClass];
      if (isTablet() && tabletClass) classes.push(tabletClass);
      if (isDesktop() && desktopClass) classes.push(desktopClass);
      return classes.join(' ');
    },
  };

  return {
    screenSize,
    isMobile,
    isTablet,
    isDesktop,
    responsive,
  };
};
